import React, { useState, useEffect } from "react";
import {
  Button,
  Form,
  Modal,
  Card,
  Badge,
  Alert,
  Dropdown,
} from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import api from "../../../../../api/axios";
import { generateDocumentAcceptanceLink } from "../../../../../utils/documentUtils";

const SendDocumentModal = ({
  show,
  candidateName,
  candidateEmail,
  candidateId,
  candidateData,
  onClose,
  onSend,
  submitting = false,
}) => {
  const [candidateDocuments, setCandidateDocuments] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [placeholderErrors, setPlaceholderErrors] = useState([]);
  const [missingPlaceholders, setMissingPlaceholders] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const schema = Yup.object({
    subject: Yup.string()
      .required("Email subject is required")
      .test(
        "no-missing-placeholders",
        "Subject contains missing placeholder values",
        function (value) {
          if (selectedTemplate && value) {
            return !value.includes("[Missing");
          }
          return true;
        }
      ),
    content: Yup.string()
      .required("Email content is required")
      .test(
        "no-missing-placeholders",
        "Message contains missing placeholder values",
        function (value) {
          if (selectedTemplate && value) {
            return !value.includes("[Missing");
          }
          return true;
        }
      ),
    documents: Yup.array().min(
      1,
      "Please select at least one document to send"
    ),
  });

  const getInitialValues = () => {
    if (selectedTemplate) {
      return {
        subject: replacePlaceholders(selectedTemplate.subject),
        content: replacePlaceholders(selectedTemplate.body),
        documents: [],
        template_id: selectedTemplate.id,
      };
    }
    return {
      subject: "",
      content: `Dear ${candidateName},\n\nPlease find the attached document(s) for your reference.\n\nBest regards,\nHR Team`,
      documents: [],
      template_id: null,
    };
  };

  // Load candidate documents and email templates when modal opens
  useEffect(() => {
    if (show && candidateId) {
      loadCandidateDocuments();
      loadEmailTemplates();
    } else if (show && !candidateId) {
      toast.error("Candidate ID is required to load documents");
    }
  }, [show, candidateId]);

  // Reprocess selected template when candidate data changes
  useEffect(() => {
    if (selectedTemplate && candidateData) {
      validatePlaceholders(selectedTemplate);
    }
  }, [candidateData, candidateName]);

  const loadCandidateDocuments = async () => {
    if (!candidateId) {
      toast.error("Candidate ID is missing");
      return;
    }

    setLoading(true);

    try {
      // Fetch documents using the provided API endpoint
      const response = await api.get(
        `/api/v2/documents/candidate/${candidateId}`
      );

      // Handle different possible response structures
      let documents = [];

      if (response.data) {
        // Try different possible data structures
        documents =
          response.data.documents ||
          response.data.data ||
          response.data.result ||
          (Array.isArray(response.data) ? response.data : []);
      }


      // Transform the data to match expected format
      const transformedDocuments = Array.isArray(documents)
        ? documents.map((doc, index) => ({
            id: doc.id || index + 1,
            title:
              doc.title ||
              doc.document_name ||
              doc.name ||
              doc.subject ||
              "Untitled Document",
            type:
              doc.type || doc.document_type || doc.template_type || "DOCUMENT",
            format: doc.format || doc.file_extension || "pdf",
            created_at:
              doc.created_at ||
              doc.createdAt ||
              doc.updated_at ||
              new Date().toISOString(),
            file_size: doc.file_size || doc.size || "Unknown",
            url:
              doc.url ||
              doc.file_url ||
              doc.download_url ||
              doc.file_path ||
              "#",
            description: doc.description || doc.content || "",
            status: doc.status || "active",
          }))
        : [];

      setCandidateDocuments(transformedDocuments);

      if (transformedDocuments.length === 0) {
        console.log("No documents found after transformation");
        // toast.info('No documents found for this candidate.');
      } else {
        console.log(`Found ${transformedDocuments.length} documents`);
      }
    } catch (error) {
      // Handle different error scenarios
      if (error.response?.status === 404) {
        // toast.info('No documents found for this candidate.');
      } else if (error.response?.status === 403) {
        toast.error(
          "Access denied. You may not have permission to view these documents."
        );
      } else if (error.response?.status >= 500) {
        toast.error("Server error. Please try again later.");
      } else {
        toast.error("Unable to load documents. Please check your connection.");
      }

      setCandidateDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const loadEmailTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const response = await api.get("/api/v2/email-templates");
      const templates =
        response.data?.data || response.data?.templates || response.data || [];
      const activeTemplates = templates.filter(
        (template) => template.is_active && (template.id === 1 || template.id === 3)
      );


      setEmailTemplates(activeTemplates);
    } catch (error) {
      toast.error("Failed to load email templates", {
        style: { zIndex: 10000 },
      });
      setEmailTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Extract all placeholders from template text
  const extractPlaceholders = (text) => {
    if (!text) return [];
    const regex = /{{([^}]+)}}/g;
    const placeholders = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (!placeholders.includes(match[1])) {
        placeholders.push(match[1]);
      }
    }
    return placeholders;
  };

  // Get candidate data mapping
  const getCandidateDataMapping = () => {
    const mapping = {
      candidate_name:
        candidateName ||
        candidateData?.full_name ||
        candidateData?.name ||
        (candidateData?.first_name && candidateData?.last_name
          ? `${candidateData.first_name} ${candidateData.last_name}`
          : null),
      company_name:
        candidateData?.company_name || candidateData?.company?.name || null,
      job_title:
        candidateData?.job_title ||
        candidateData?.position_title ||
        candidateData?.designation ||
        candidateData?.job?.title ||
        candidateData?.position ||
        null,
      hr_name:
        candidateData?.hr_name ||
        candidateData?.recruiter_name ||
        candidateData?.hr_contact ||
        candidateData?.created_by_name ||
        "HR Team",
      interview_date:
        candidateData?.interview_date ||
        candidateData?.next_interview_date ||
        null,
      interview_time:
        candidateData?.interview_time ||
        candidateData?.next_interview_time ||
        null,
      interview_location:
        candidateData?.interview_location ||
        candidateData?.next_interview_location ||
        null,
      interviewer_name:
        candidateData?.interviewer_name ||
        candidateData?.next_interviewer ||
        null,
    };

    return mapping;
  };

  // Validate placeholders and detect missing values
  const validatePlaceholders = (template) => {
    const subjectPlaceholders = extractPlaceholders(template?.subject);
    const bodyPlaceholders = extractPlaceholders(template?.body);
    const allPlaceholders = [
      ...new Set([...subjectPlaceholders, ...bodyPlaceholders]),
    ];

    const candidateMapping = getCandidateDataMapping();
    const missing = [];
    const errors = [];

    allPlaceholders.forEach((placeholder) => {
      const value = candidateMapping[placeholder];

      if (!value || value === null) {
        missing.push(placeholder);
        errors.push({
          placeholder: placeholder,
          message: `Missing ${placeholder.replace(/_/g, " ").toLowerCase()}`,
          field: placeholder,
        });
      }
    });

    setMissingPlaceholders(missing);
    setPlaceholderErrors(errors);

    return { isValid: missing.length === 0, missing, errors };
  };

  const replacePlaceholders = (template) => {
    if (!template) return "";

    const candidateMapping = getCandidateDataMapping();

    let result = template;
    Object.entries(candidateMapping).forEach(([key, value]) => {
      const placeholder = `{{${key}}}`;
      const replacementValue =
        value || `[Missing ${key.replace(/_/g, " ").toLowerCase()}]`;

      // Use simple string replacement for better reliability
      while (result.includes(placeholder)) {
        result = result.replace(placeholder, replacementValue);
      }

    });

    return result;
  };

  const handleTemplateSelect = (templateId, setFieldValue) => {
    const template = emailTemplates.find((t) => t.id === parseInt(templateId));
    setSelectedTemplate(template);

    if (template) {
      // Validate placeholders before setting values
      const validation = validatePlaceholders(template);

      setFieldValue("subject", replacePlaceholders(template.subject));
      setFieldValue("content", replacePlaceholders(template.body));
      setFieldValue("template_id", template.id);

      // Show warning if there are missing placeholders
      if (!validation.isValid) {
        console.log(
          `Template selected but missing candidate data: ${validation.missing
            .map((p) => p.replace(/_/g, " "))
            .join(", ")}`
        );
      }
    } else {
      // Clear placeholders when no template selected
      setMissingPlaceholders([]);
      setPlaceholderErrors([]);
    }
  };

  const handleDocumentSelect = async (document, setFieldValue) => {
    let updatedSelection;

    if (selectedDocuments.some((doc) => doc.id === document.id)) {
      // Remove document if already selected
      updatedSelection = selectedDocuments.filter(
        (doc) => doc.id !== document.id
      );
    } else {
      // Add document if not selected
      updatedSelection = [...selectedDocuments, document];
    }

    setSelectedDocuments(updatedSelection);
    setFieldValue("documents", updatedSelection);

    // Auto-generate acceptance links and insert into email content
    await updateEmailContentWithAcceptanceLinks(
      updatedSelection,
      setFieldValue
    );
  };

  const toggleSelectAll = async (setFieldValue) => {
    let updatedSelection;
    if (selectedDocuments.length === candidateDocuments.length) {
      // Deselect all
      updatedSelection = [];
      setSelectedDocuments([]);
      setFieldValue("documents", []);
    } else {
      // Select all
      updatedSelection = [...candidateDocuments];
      setSelectedDocuments([...candidateDocuments]);
      setFieldValue("documents", [...candidateDocuments]);
    }

    // Update email content with acceptance links
    await updateEmailContentWithAcceptanceLinks(
      updatedSelection,
      setFieldValue
    );
  };

  const updateEmailContentWithAcceptanceLinks = async (
    documents,
    setFieldValue
  ) => {
    // Get current content and remove any existing acceptance sections
    const currentContent =
      document.getElementById("email-content-field")?.value || "";
    let baseContent = currentContent.split("\nDocument Acceptance:")[0]; // Remove existing section
    baseContent = baseContent.split("\n\nDocument Acceptance:")[0]; // Remove with double newline too

    if (documents.length === 0) {
      // If no documents selected, just set the base content
      setFieldValue("content", baseContent);
      return;
    }

    try {
      const companyId = candidateData?.company_id || 1;
      let acceptanceLinksText = "\n\nDocument Acceptance:\n";

      for (const document of documents) {
        // Create individual acceptance link for each document
        const acceptanceLink = generateDocumentAcceptanceLink(
          candidateId,
          companyId,
          document.id
        );
        acceptanceLinksText += `• ${document.title} - <a href="${acceptanceLink}">Accept</a>\n`;
      }

      // Common email footers to detect
      const footerPatterns = [
        /\n\n(Best regards?,?[\s\S]*)/i,
        /\n\n(Sincerely,?[\s\S]*)/i,
        /\n\n(Kind regards?,?[\s\S]*)/i,
        /\n\n(Regards?,?[\s\S]*)/i,
        /\n\n(Thank you,?[\s\S]*)/i,
        /\n\n(Thanks,?[\s\S]*)/i,
        /\n\n(Warm regards?,?[\s\S]*)/i,
        /\n\n(Yours truly,?[\s\S]*)/i,
        /\n\n(Yours sincerely,?[\s\S]*)/i,
      ];

      let updatedContent = baseContent;
      let footerFound = false;

      // Check if any footer pattern exists
      for (const pattern of footerPatterns) {
        const match = baseContent.match(pattern);
        if (match) {
          // Insert acceptance links before the footer
          const beforeFooter = baseContent.substring(0, match.index);
          const footer = match[0];
          updatedContent = beforeFooter + acceptanceLinksText + footer;
          footerFound = true;
          break;
        }
      }

      // If no footer found, append links at the end
      if (!footerFound) {
        updatedContent = baseContent + acceptanceLinksText;
      }

      setFieldValue("content", updatedContent);
    } catch (error) {
      toast.error("Failed to generate document acceptance links", {
        style: { zIndex: 10000 },
      });
    }
  };

  const handlePreviewDocument = (document) => {
    // Open document in new tab for preview
    window.open(document.url, "_blank");
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleModalClose = () => {
    setSelectedDocuments([]);
    setCandidateDocuments([]);
    setSelectedTemplate(null);
    setEmailTemplates([]);
    setPlaceholderErrors([]);
    setMissingPlaceholders([]);
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={handleModalClose}
      centered
      size="xl"
      backdrop="static"
    >
      <Modal.Header  className="py-2">
        <Modal.Title className="fs-5">
          <i className="fa fa-paper-plane me-2"></i>
          Send Document
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-3">
        <Formik
          initialValues={getInitialValues()}
          validationSchema={schema}
          enableReinitialize={true}
          onSubmit={async (values, helpers) => {
            try {
              const payload = {
                ...values,
                documents: selectedDocuments,
                recipient_email: candidateEmail,
                recipient_name: candidateName,
              };
              await onSend(payload);
              helpers.resetForm();
            } finally {
              helpers.setSubmitting(false);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => {
            // Update documents field when selectedDocuments changes
            if (values.documents.length !== selectedDocuments.length) {
              setFieldValue("documents", selectedDocuments);
            }

            return (
              <Form noValidate onSubmit={handleSubmit}>
                <Card className="mb-3">
                  <Card.Body className="p-3">
                    {/* Document Selection */}
                    {loading ? (
                      <div className="text-center py-3">
                        <i className="fa fa-spinner fa-spin"></i>
                        <div className="small mt-2">Loading documents...</div>
                      </div>
                    ) : (
                      <>
                        <div>
                          <Form.Group className="mb-2">
                            <Form.Label className="mb-1 small fw-semibold">
                              Email Template:{" "}
                              <span className="text-muted">(Optional)</span>
                            </Form.Label>
                            <div className="d-flex">
                              <Form.Select
                                size="sm"
                                value={selectedTemplate?.id || ""}
                                onChange={(e) =>
                                  handleTemplateSelect(
                                    e.target.value,
                                    setFieldValue
                                  )
                                }
                                disabled={templatesLoading}
                                className="me-2"
                              >
                                <option value="">Select a template...</option>
                                {emailTemplates.map((template) => (
                                  <option key={template.id} value={template.id}>
                                    {template.template_key
                                      .replace(/_/g, " ")
                                      .toLowerCase()
                                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                                  </option>
                                ))}
                              </Form.Select>
                              {selectedTemplate && (
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedTemplate(null);
                                    setFieldValue("subject", "");
                                    setFieldValue(
                                      "content",
                                      `Dear ${candidateName},\n\nPlease find the attached document(s) for your reference.\n\nBest regards,\nHR Team`
                                    );
                                    setFieldValue("template_id", null);
                                    setPlaceholderErrors([]);
                                    setMissingPlaceholders([]);
                                  }}
                                  title="Clear template"
                                >
                                  <i className="fa fa-times"></i>
                                </Button>
                              )}
                            </div>
                            {templatesLoading && (
                              <div className="small text-muted mt-1">
                                <i className="fa fa-spinner fa-spin me-1"></i>
                                Loading templates...
                              </div>
                            )}
                            {selectedTemplate && (
                              <Alert
                                variant={
                                  placeholderErrors.length > 0
                                    ? "warning"
                                    : "light"
                                }
                                className="mt-2 mb-0 py-2"
                              >
                                <small>
                                  <i
                                    className={`fa ${
                                      placeholderErrors.length > 0
                                        ? "fa-exclamation-triangle text-warning"
                                        : "fa-info-circle text-primary"
                                    } me-1`}
                                  ></i>
                                  <strong>
                                    {selectedTemplate.template_key
                                      .replace(/_/g, " ")
                                      .toLowerCase()
                                      .replace(/\b\w/g, (l) =>
                                        l.toUpperCase()
                                      )}{" "}
                                    Template
                                  </strong>{" "}
                                  selected.
                                  {placeholderErrors.length === 0 ? (
                                    <span>
                                      {" "}
                                      Subject and message have been updated with
                                      template content.
                                    </span>
                                  ) : (
                                    <div className="mt-1">
                                      <div>
                                        Missing candidate information for:
                                      </div>
                                      <ul className="mb-0 mt-1 ps-3">
                                        {placeholderErrors.map(
                                          (error, index) => (
                                            <li key={index} className="small">
                                              <strong>
                                                {error.placeholder
                                                  .replace(/_/g, " ")
                                                  .toLowerCase()}
                                              </strong>
                                            </li>
                                          )
                                        )}
                                      </ul>
                                      <div className="mt-1 text-muted">
                                        Please update candidate profile or
                                        manually edit the email content.
                                      </div>
                                    </div>
                                  )}
                                </small>
                              </Alert>
                            )}
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label className="mb-2 fw-semibold">
                              <i className="fa fa-files-o me-2"></i>
                              Select Documents:{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>

                            <Dropdown
                              show={dropdownOpen}
                              onToggle={setDropdownOpen}
                            >
                              <Dropdown.Toggle
                                variant={
                                  touched.documents && errors.documents
                                    ? "outline-danger"
                                    : "outline-secondary"
                                }
                                className="w-100 d-flex justify-content-between align-items-center"
                                disabled={candidateDocuments.length === 0}
                              >
                                <span>
                                  {selectedDocuments.length === 0
                                    ? "Select documents..."
                                    : selectedDocuments.length === 1
                                    ? `${selectedDocuments[0].title}`
                                    : `${selectedDocuments.length} documents selected`}
                                </span>
                              </Dropdown.Toggle>

                              <Dropdown.Menu
                                className="w-100"
                                style={{
                                  maxHeight: "300px",
                                  overflowY: "auto",
                                }}
                              >
                                {candidateDocuments.length > 1 && (
                                  <>
                                    <Dropdown.Item
                                      as="div"
                                      className="d-flex align-items-center p-2"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        toggleSelectAll(setFieldValue);
                                      }}
                                    >
                                      <Form.Check
                                        type="checkbox"
                                        checked={
                                          selectedDocuments.length ===
                                          candidateDocuments.length
                                        }
                                        onChange={() =>
                                          toggleSelectAll(setFieldValue)
                                        }
                                        className="me-2"
                                        label={
                                          selectedDocuments.length ===
                                          candidateDocuments.length
                                            ? "Deselect All"
                                            : "Select All"
                                        }
                                      />
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                  </>
                                )}

                                {candidateDocuments.length > 0 ? (
                                  candidateDocuments.map((document) => (
                                    <Dropdown.Item
                                      key={document.id}
                                      as="div"
                                      className="p-2"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleDocumentSelect(
                                          document,
                                          setFieldValue
                                        );
                                      }}
                                    >
                                      <div className="d-flex align-items-center">
                                        <Form.Check
                                          type="checkbox"
                                          checked={selectedDocuments.some(
                                            (doc) => doc.id === document.id
                                          )}
                                          onChange={() =>
                                            handleDocumentSelect(
                                              document,
                                              setFieldValue
                                            )
                                          }
                                          className="me-3"
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                        <div className="flex-grow-1">
                                          <div className="d-flex align-items-center justify-content-between">
                                            <div>
                                              <div className="d-flex align-items-center mb-1">
                                                <h6 className="mb-0 small fw-semibold text-primary">
                                                  {document.title}
                                                </h6>
                                                <Badge
                                                  bg={
                                                    document.type === "OFFER"
                                                      ? "success"
                                                      : "info"
                                                  }
                                                  className="ms-2 small"
                                                >
                                                  {document.type}
                                                </Badge>
                                              </div>
                                              <div className="small text-muted">
                                                <i className="fa fa-file-pdf-o me-1 text-danger"></i>
                                                <span className="me-2">
                                                  {document.format.toUpperCase()}
                                                </span>
                                                <span className="me-2">
                                                  {document.file_size}
                                                </span>
                                                <span>
                                                  {formatDate(
                                                    document.created_at
                                                  )}
                                                </span>
                                              </div>
                                            </div>
                                            <Button
                                              variant="outline-primary"
                                              size="sm"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handlePreviewDocument(document);
                                              }}
                                              title="Preview document"
                                            >
                                              <i className="fa fa-eye"></i>
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    </Dropdown.Item>
                                  ))
                                ) : (
                                  <Dropdown.Item disabled>
                                    <div className="text-center text-muted py-2">
                                      <i className="fa fa-info-circle me-1"></i>
                                      No documents available
                                    </div>
                                  </Dropdown.Item>
                                )}
                              </Dropdown.Menu>
                            </Dropdown>

                            {touched.documents && errors.documents && (
                              <div className="text-danger small mt-1">
                                {errors.documents}
                              </div>
                            )}

                            {/* Selected documents summary */}
                            {selectedDocuments.length > 0 && (
                              <div className="mt-2 p-2 bg-light rounded border">
                                <div className="small fw-semibold text-muted mb-2">
                                  Selected Documents ({selectedDocuments.length}
                                  ):
                                </div>
                                <div className="d-flex flex-wrap gap-1">
                                  {selectedDocuments.map((document, index) => (
                                    <Badge
                                      key={document.id}
                                      bg="secondary"
                                      className="d-flex align-items-center"
                                    >
                                      {document.title}
                                      <button
                                        type="button"
                                        className="btn-close btn-close-white ms-1"
                                        style={{ fontSize: "0.6rem" }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          handleDocumentSelect(
                                            document,
                                            setFieldValue
                                          );
                                        }}
                                        title="Remove document"
                                      ></button>
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {touched.documents && errors.documents && (
                              <div className="text-danger small mt-1">
                                {errors.documents}
                              </div>
                            )}

                            {candidateDocuments.length === 0 && (
                              <Alert variant="info" className="mt-2 mb-0">
                                <small>
                                  <i className="fa fa-info-circle me-1"></i>
                                  No documents found for this candidate. Please
                                  create a document first.
                                </small>
                              </Alert>
                            )}
                          </Form.Group>
                          <Form.Group className="mb-2">
                            <Form.Label className="mb-1 small fw-semibold">
                              To:
                            </Form.Label>
                            <Form.Control
                              size="sm"
                              type="email"
                              value={`${candidateName} <${candidateEmail}>`}
                              disabled
                              className="bg-light"
                            />
                          </Form.Group>

                          <Form.Group className="mb-2">
                            <Form.Label className="mb-1 small fw-semibold">
                              Subject: <span className="text-danger">*</span>
                              {values.subject &&
                                values.subject.includes("[Missing") && (
                                  <Badge bg="warning" className="ms-2 small">
                                    Missing Data
                                  </Badge>
                                )}
                            </Form.Label>
                            <Form.Control
                              size="sm"
                              type="text"
                              name="subject"
                              value={values.subject}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.subject && !!errors.subject}
                              className={
                                values.subject &&
                                values.subject.includes("[Missing")
                                  ? "border-warning"
                                  : ""
                              }
                              placeholder="Enter email subject"
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.subject}
                            </Form.Control.Feedback>
                            {values.subject &&
                              values.subject.includes("[Missing") && (
                                <div className="small text-warning mt-1">
                                  <i className="fa fa-exclamation-triangle me-1"></i>
                                  Subject contains placeholder values that need
                                  candidate data.
                                </div>
                              )}
                          </Form.Group>

                          <Form.Group className="mb-2">
                            <Form.Label className="mb-1 small fw-semibold">
                              Message: <span className="text-danger">*</span>
                              {values.content &&
                                values.content.includes("[Missing") && (
                                  <Badge bg="warning" className="ms-2 small">
                                    Missing Data
                                  </Badge>
                                )}
                            </Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={8}
                              name="content"
                              id="email-content-field"
                              value={values.content}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.content && !!errors.content}
                              className={
                                values.content &&
                                values.content.includes("[Missing")
                                  ? "border-warning"
                                  : ""
                              }
                              placeholder="Enter your message"
                              style={{ fontSize: "14px" }}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.content}
                            </Form.Control.Feedback>
                            {values.content &&
                              values.content.includes("[Missing") && (
                                <div className="small text-warning mt-1">
                                  <i className="fa fa-exclamation-triangle me-1"></i>
                                  Message contains placeholder values that need
                                  candidate data.
                                </div>
                              )}
                          </Form.Group>

                          {selectedDocuments.length > 0 && (
                            <Alert variant="success" className="mt-3 mb-0 py-2">
                              <small>
                                <i className="fa fa-paperclip me-1"></i>
                                <strong>
                                  Attachments ({selectedDocuments.length}):
                                </strong>
                                <div className="mt-1">
                                  {selectedDocuments.map((doc, index) => (
                                    <span
                                      key={doc.id}
                                      className="d-inline-block me-2 mb-1"
                                    >
                                      {doc.title}
                                      <Badge
                                        bg="secondary"
                                        className="ms-1 small"
                                      >
                                        {doc.type}
                                      </Badge>
                                      {index < selectedDocuments.length - 1 && (
                                        <span className="text-muted">, </span>
                                      )}
                                    </span>
                                  ))}
                                </div>
                              </small>
                            </Alert>
                          )}
                        </div>
                      </>
                    )}
                  </Card.Body>
                </Card>

                <div className="d-flex justify-content-end gap-2 mt-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleModalClose}
                    disabled={isSubmitting || submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    type="submit"
                    disabled={
                      isSubmitting ||
                      submitting ||
                      selectedDocuments.length === 0
                    }
                  >
                    {isSubmitting || submitting ? (
                      <>
                        <i className="fa fa-spinner fa-spin me-2"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-paper-plane me-2"></i>
                        Send Email ({selectedDocuments.length}{" "}
                        {selectedDocuments.length === 1
                          ? "document"
                          : "documents"}
                        )
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default SendDocumentModal;
